import React, { useState, useContext, useEffect } from "react";
import { GlobalContext } from "../contexts/GlobalContext";
import api from "../axios/api";
import axios from "axios";
import { toast } from "react-toastify";
import CoverPhoto from "../assets/images/banner/cover.jpg";
import CompanyProfile from "./CompanyProfile";
import {
  FaDollarSign,
  FaPhone,
  FaCheckCircle,
  FaEnvelope,
  FaTree,
  FaGift,
  FaBuilding,
  FaMapMarkerAlt,
} from "react-icons/fa";
import * as Yup from "yup";
import {
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import ChangePassword from "./ChangePassword";
import Button from "../components/common/styled components/Button";
import Loader from "../components/common/Loader";

const EditProfile = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const { isLoggedIn, setLoggedIn, setProfilePicture } =
    useContext(GlobalContext);
  const [imagePreview, setImagePreview] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);
  const [collectedPoints, setCollectedPoints] = useState(0);
  const [redeemedPoints, setRedeemedPoints] = useState(0);
  const [redeemptionPoints, setRedeepmtionPoints] = useState(0);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Name is required"),
    phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^\d+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .max(12, "Phone number must be at most 12 digits"),
    ...(editedUser?.role_code === "corporate" && {
      company_name: Yup.string().required("Company name is required"),
      industry_type_id: Yup.string().required("Industry type is required"),
      website: Yup.string().url("Must be a valid URL").nullable(),
    }),
    ...(editedUser?.role_code === "employee" && {
      organization_id: Yup.string().required("Company is required"),
    }),
  });

  const fetchIndustryTypes = async () => {
    try {
      const response = await api.get("/api/user/industrytypes");
      setIndustryTypeOptions(response.data[0]);
    } catch (error) {
      console.error("Error fetching Organization Data:", error);
      toast.error("Failed to load organization options. Please try again.");
    }
  };

  useEffect(() => {
    fetchIndustryTypes();
    const fetchData = async () => {
      try {
        const response = await api.get("/api/user/userdetails");
        if (response.data.isLoggedIn) {
          const userData = response.data.user.user;
          setUser(userData);
          setEditedUser(userData);
          setCollectedPoints(response.data.user.user.collected_points);
          setRedeemedPoints(response.data.user.user.redeemed_points);
          setRedeepmtionPoints(
            response.data.user.user.redeemption_requested_point
          );
          setProfilePicture(userData.userProfilePic);
          setLoggedIn(true);

          if (userData.role_code === "employee") {
            const orgResponse = await api.get("/api/user/organizations");
            setOrganizations(orgResponse.data[0]);
          }
        } else {
          setLoggedIn(false);
        }
      } catch (error) {
        console.error("Error fetching user data", error);
        setLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoggedIn, setProfilePicture]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "userEmail") return;
    setEditedUser((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await validationSchema.validate(editedUser, { abortEarly: false });

      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("userName", editedUser.full_name);
      formData.append("phoneNumber", editedUser.phoneNumber);

      if (editedUser.userProfilePic instanceof File) {
        formData.append("profilePicture", editedUser.userProfilePic);
      }
      if (editedUser.userCoverPic instanceof File) {
        formData.append("coverImage", editedUser.userCoverPic);
      }
      if (editedUser.role_code === "employee") {
        formData.append("organizationId", editedUser.organization_id);
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/user/update/${user.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setUser({ ...editedUser });
        setIsEditing(false);
        toast.success("Profile updated successfully");
        window.location.reload();
      } else {
        console.error("Error updating profile:", response.data.message);
        toast.error("Failed to update profile. Please try again.");
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        console.error("Error updating profile:", error);
        toast.error("An error occurred while updating your profile.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = () => setIsEditing(true);
  
  const handleCancel = () => {
    // Reset to original user data
    setEditedUser({ ...user });
    setIsEditing(false);
    
    // Clear image previews
    setImagePreview(null);
    setCoverImagePreview(null);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedUser((prev) => ({ ...prev, userProfilePic: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedUser((prev) => ({ ...prev, userCoverPic: file }));
      setCoverImagePreview(URL.createObjectURL(file));
    }
  };

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return <Loader />;
  }

  if (!isLoggedIn || !user) {
    return <div className="text-center text-lg text-gray-600 p-10">You are not logged in.</div>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(user.role_code === "corporate" || user.role_code === 'ngo') ? (
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          centered
          sx={{ marginBottom: 2 }}
        >
          <Tab
            label="User Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Company Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Change Password"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
        </Tabs>
      ) : (
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          centered
          sx={{
            marginBottom: 2,
            '& .MuiTabs-indicator': {
              display: 'none', // hides the green indicator completely
            },
          }}
        >
          <Tab
            label="User Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              borderRadius: 0,
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Change Password"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              borderRadius: 0,
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
        </Tabs>
      )}

      {activeTab === 0 ? (
        // Profile tab content with Tailwind
        <div className="mx-auto w-full max-w-2xl bg-white rounded-lg shadow-md overflow-hidden">
          {/* Cover Photo */}
          <div className="relative h-32 md:h-40">
            {isEditing ? (
              <label htmlFor="coverPhotoInput" className="relative block h-32 md:h-40 cursor-pointer">
                {/* Image */}
                <img
                  src={coverImagePreview || user.userCoverPic || CoverPhoto}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-transparent hover:bg-black/20 transition-colors duration-300" />

                {/* Persistent label */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-sm md:text-base text-center p-2 z-10">
                  Edit Cover Photo
                </div>

                {/* Hidden input */}
                <input
                  type="file"
                  accept="image/*"
                  id="coverPhotoInput"
                  onChange={handleCoverImageChange}
                  className="hidden"
                />
              </label>
            ) : (
              // Non-editing state
              <div className="h-32 md:h-40">
                <img
                  src={user.userCoverPic || CoverPhoto}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>


          <div className="px-4 py-3 md:px-6 md:py-5">
            {/* Profile Header */}
            <div className="flex flex-col items-center mb-4 md:mb-6">
              {/* Profile Picture */}
              <div className="relative w-24 h-24 md:w-28 md:h-28 -mt-12 mb-3">
                {isEditing ? (
                  <label htmlFor="profilePictureInput" className="relative block w-full h-full cursor-pointer">
                    {/* Profile Image */}
                    <img
                      src={imagePreview || user.userProfilePic || "/images/sprout.png"}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover border-4 border-white shadow-sm"
                    />

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 rounded-full bg-transparent hover:bg-black/30 transition-colors duration-300" />

                    {/* Persistent Bottom Label */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center px-2 py-1 rounded-b-full z-10">
                      Edit Profile Picture
                    </div>

                    {/* Hidden File Input */}
                    <input
                      type="file"
                      accept="image/*"
                      id="profilePictureInput"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <img
                    src={user.userProfilePic || "/images/sprout.png"}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover border-4 border-white shadow-sm"
                  />
                )}
              </div>


              {/* Name and Wallet */}
              <div className="text-center w-full">
                <h2 className="text-xl font-bold text-gray-800 mb-1">{user.full_name}</h2>
                <p className="text-sm text-gray-600 mb-2">Role: {user.role_name}</p>

                <div className="flex items-center justify-center space-x-1 text-sm">
                  <span className="text-gray-700">Current Balance:</span>
                  <span className="font-semibold text-gray-800">
                    {collectedPoints > 9000
                      ? `${Math.floor(collectedPoints / 1000)}K`
                      : collectedPoints}
                  </span>
                  <FaDollarSign className="text-green-600 text-sm" />
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              {isEditing ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    NOTE: fields marked with <span className="text-red-500">*</span> are mandatory to input.
                  </p>

                  <div className="space-y-1">
                    <label htmlFor="userName" className="block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={editedUser.full_name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.full_name ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-green-500"
                        }`}
                    />
                    {errors.full_name && (
                      <span className="text-xs text-red-500">{errors.full_name}</span>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      
                      value={editedUser.phoneNumber}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phoneNumber ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-green-500"
                        }`}
                    />
                    {errors.phoneNumber && (
                      <span className="text-xs text-red-500">{errors.phoneNumber}</span>
                    )}
                  </div>

                  {user.role_code === "employee" && (
                    <div className="space-y-1">
                      <label htmlFor="organization" className="block text-sm font-medium text-gray-700">Company</label>
                      <select
                        name="organization_id"
                        value={editedUser.organization_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.organization_id ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-green-500"
                          }`}
                      >
                        <option value="">Select Company</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.company_name}
                          </option>
                        ))}
                      </select>
                      {errors.organization_id && (
                        <span className="text-xs text-red-500">{errors.organization_id}</span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                    <div className="flex items-center space-x-2">
                      <FaEnvelope className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Email:</span>
                      <span className="text-sm text-gray-600 truncate">{user.userEmail}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaPhone className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Phone:</span>
                      <span className="text-sm text-gray-600">{user.phoneNumber || ""}</span>
                    </div>

                    {user.role_code === "employee" && (
                      <div className="flex items-center space-x-2 col-span-2">
                        <FaBuilding className="text-gray-600 text-sm flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700">Company:</span>
                        <span className="text-sm text-gray-600">{user.company_name || ""}</span>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <FaTree className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Total Trees:</span>
                      <span className="text-sm text-gray-600">{user.totalTrees}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Verified Trees:</span>
                      <span className="text-sm text-gray-600">{user.verifiedTrees}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaMapMarkerAlt className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Geotagged Trees:</span>
                      <span className="text-sm text-gray-600">{user.geoLocatedTreesCount}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaGift className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Rewarded points:</span>
                      <span className="text-sm text-gray-600">{redeemedPoints}</span>
                    </div>

                    <div className="flex items-center space-x-2 col-span-2">
                      <FaDollarSign className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Redemption Requested points:</span>
                      <span className="text-sm text-gray-600">{redeemptionPoints}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center md:justify-end space-x-3">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={submitting}
                  >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                  </Button>
                  <Button
                    variant="cancel"
                    onClick={handleCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 1 && (user.role_code === "corporate" || user.role_code === 'ngo') ? (
        <CompanyProfile />
      ) : (
        <ChangePassword />
      )}
    </Box>
  );
};

export default EditProfile;